"use client";

import React from "react";
import { Globe, Mail, Phone, MapPin, Building, ShieldCheck, CheckCircle2, ArrowRight } from "lucide-react";
import { StatusBadge } from "./technical-components";

interface FoundersStorySectionProps {
  onOpenDemo: () => void;
}

export function FoundersStorySection({ onOpenDemo }: FoundersStorySectionProps) {
  const founders = [
    {
      name: "Animesh Bhaskar",
      title: "CO-FOUNDER // BUSINESS & INSTITUTIONAL STRATEGY",
      image: "/media/animesh_bhaskar.jpeg",
      credentials: "PGDM — IIM Jammu (Batch of 2021)",
      email: "animeshbhaskar9@gmail.com",
      phone: "+91 7051594695",
      linkedin: "https://linkedin.com",
      bio: "Animesh Bhaskar is a business and program management professional driven by one belief: the next generation of sporting talent deserves a smarter ecosystem to grow in. With experience spanning sales, marketing, retail and large-scale program management, Animesh has spent his career building and scaling programs across diverse markets. At Olympio, he leads business strategy, partnerships, and market development—helping schools digitalize, manage, and monetize their sporting ecosystem.",
      specialization: "Business Strategy, Campus Partnerships & Market Development",
    },
    {
      name: "Neelesh Tiwari",
      title: "CO-FOUNDER // AI & CLOUD SOLUTION ARCHITECT",
      image: "/media/neelesh_tiwari.jpeg",
      credentials: "Masters in Artificial Intelligence & Robotics // MNC AI & Cloud Engineer",
      email: "CEO@olympio.in",
      phone: "+91 9625825683",
      linkedin: "https://www.linkedin.com/in/neelesh-t-3a7599a2",
      bio: "Neelesh Tiwari is an IT, Cloud, and Software Solution Architect specializing in Artificial Intelligence, MLOps, and enterprise cloud infrastructure (AWS Lambda, EC2, S3). Serial tech entrepreneur and engineer who developed machine learning-integrated mobile applications, CI/CD automation pipelines, and high-performance biometric algorithms. Architect of Olympio's AI biometric talent discovery radar, Mirwald PHV engine, and secure cloud SaaS platform.",
      specialization: "AI/MLOps Architecture, Cloud Infrastructure & Biometric Data Pipelines",
    },
    {
      name: "Mohit Saini",
      title: "CO-FOUNDER // NATIONAL OPERATIONS & PROGRAM MANAGER",
      image: "/media/mohit_saini.jpeg",
      credentials: "IIM Sirmaur Alumni // Ex-BYJU'S (BTL) & Ex-Founder Compass Marketing",
      email: "mohit.saini499@gmail.com",
      phone: "+91 9958382933",
      linkedin: "https://www.linkedin.com/in/mohit-saini-b8892aa1",
      bio: "Mohit Saini is a National Program Manager and ex-Founder who understands that businesses don't run on theories—they run on execution. Currently driving sales operations managing a team of 1,000+ promoters across 4,000+ stores. Ex-BYJU'S BTL leader and founder of Compass Marketing. At Olympio, Mohit bridges ground-level campus operations, BTL activation funnels, facility rental monetization, and Generative AI/LLM data accuracy.",
      specialization: "National Scale Operations, BTL Activation & Generative AI Prompt Logic",
    },
  ];

  return (
    <section id="founders" className="py-24 border-b border-[#3A3A38]/20 bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <StatusBadge text="FOUNDER MANIFESTO &amp; LEADERSHIP" />
          <h2 className="font-space font-bold text-4xl sm:text-6xl text-[#1A3C2B] tracking-tight uppercase leading-[0.95]">
            FOUNDERS &amp; EXECUTIVE BOARD.
          </h2>
          <p className="font-mono-tech text-xs text-[#3A3A38] uppercase tracking-wider leading-relaxed">
            BUILT BY ATHLETES, IIM ALUMNI, AND MNC AI SOLUTION ARCHITECTS HEADQUARTERED IN GREATER NOIDA WEST, NCR.
          </p>
        </div>

        {/* Founding Manifesto Banner */}
        <div className="border border-[#3A3A38]/30 bg-[#F7F7F5] p-8 sm:p-10 relative font-mono-tech">
          {/* L-Corner Markers */}
          <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t-2 border-l-2 border-[#1A3C2B]" />
          <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t-2 border-r-2 border-[#1A3C2B]" />
          <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b-2 border-l-2 border-[#1A3C2B]" />
          <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b-2 border-r-2 border-[#1A3C2B]" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-4">
              <span className="text-[10px] text-[#1A3C2B] font-bold uppercase tracking-widest bg-[#1A3C2B]/10 px-2.5 py-1 inline-block">
                MANIFESTO // THE OLYMPIO VISION
              </span>
              <h3 className="font-space font-bold text-2xl sm:text-3xl text-[#1A3C2B] uppercase leading-tight">
                &ldquo;WHY SHOULD EUROPEAN AND AMERICAN ACADEMIES HAVE BIOMETRIC RADARS, WHILE OUR SCHOOL CHILDREN ARE JUDGED ON PAPER CLIPBOARDS?&rdquo;
              </h3>
              <p className="text-xs text-[#3A3A38] leading-relaxed">
                Over 100 million youth attend schools across India daily. Among them are children possessing world-class sprint latencies, extraordinary spatial coordination, and boundless stamina. Olympio bridges this gap by turning school playgrounds into intelligent operating systems—measuring athletic talent early while monetizing campus sports facilities after-hours.
              </p>
              <div className="flex flex-wrap gap-4 text-[10px] text-[#1A3C2B] pt-2">
                <span className="flex items-center gap-1">
                  <Building className="w-3.5 h-3.5 text-[#1A3C2B]" />
                  TECHZONE 4, GREATER NOIDA WEST, NCR
                </span>
                <span>•</span>
                <span>REGISTERED ENTITY: OLYMPIO PVT. LTD.</span>
              </div>
            </div>

            <div className="lg:col-span-4 border border-[#3A3A38]/20 bg-white p-6 space-y-3 text-center">
              <span className="text-[10px] text-[#1A3C2B] font-bold uppercase tracking-widest block">
                FOUNDER SCHOOL PARTNERSHIP
              </span>
              <h4 className="font-space font-bold text-lg text-[#1A3C2B] uppercase">
                50 FOUNDER SCHOOLS PROGRAM
              </h4>
              <p className="text-[11px] text-[#3A3A38] leading-tight">
                Establishing modern biometric athletic command centers in 50 pioneer K-12 campuses across India.
              </p>
              <button
                onClick={onOpenDemo}
                className="w-full py-2.5 bg-[#1A3C2B] text-white hover:bg-[#132d20] transition uppercase font-bold text-xs rounded-none"
              >
                APPLY AS A FOUNDER SCHOOL
              </button>
            </div>
          </div>
        </div>

        {/* 3 Founders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {founders.map((founder, idx) => (
            <div
              key={idx}
              className="border border-[#3A3A38]/30 bg-[#F7F7F5] p-6 flex flex-col justify-between space-y-6 relative rounded-none hover:border-[#1A3C2B] transition-colors"
            >
              {/* L-Corner Markers */}
              <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-[#1A3C2B]" />
              <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-[#1A3C2B]" />

              <div className="space-y-4">
                {/* Image Avatar (Square, 0px radius, mix-blend-luminosity) */}
                <div className="w-full aspect-square border border-[#3A3A38]/30 overflow-hidden bg-white relative">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={founder.image}
                    alt={founder.name}
                    className="w-full h-full object-cover img-luminosity"
                  />
                </div>

                {/* Info Header */}
                <div className="space-y-1 font-mono-tech">
                  <h3 className="font-space font-bold text-xl text-[#1A3C2B] uppercase">
                    {founder.name}
                  </h3>
                  <p className="text-[10px] text-[#1A3C2B] font-bold uppercase tracking-wider">
                    {founder.title}
                  </p>
                  <p className="text-[10px] text-[#3A3A38]/70 uppercase">
                    {founder.credentials}
                  </p>
                </div>

                {/* Bio text */}
                <p className="font-mono-tech text-xs text-[#3A3A38] leading-relaxed">
                  {founder.bio}
                </p>

                {/* Specialization Box */}
                <div className="border-t border-[#3A3A38]/20 pt-3 space-y-1 font-mono-tech text-[10px]">
                  <div className="text-[#1A3C2B] font-bold uppercase flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-[#1A3C2B]" />
                    <span>SPECIALIZATION:</span>
                  </div>
                  <div className="text-[#3A3A38]">{founder.specialization}</div>
                </div>
              </div>

              {/* Contact Footer */}
              <div className="border-t border-[#3A3A38]/20 pt-3 flex items-center justify-between font-mono-tech text-[10px] text-[#1A3C2B]">
                <a href={`mailto:${founder.email}`} className="hover:underline flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  <span className="truncate max-w-[130px]">{founder.email}</span>
                </a>
                <a href={founder.linkedin} target="_blank" rel="noreferrer" className="hover:underline flex items-center gap-1 font-bold">
                  <Globe className="w-3 h-3" />
                  <span>LINKEDIN</span>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
